/**
 * Previs skin (design §19) — a structured mapping of the level vocabulary to the GAME'S
 * look, so the viewports' game view renders like the built game instead of Bumble's
 * hardcoded colors. Two derivation sources, best-first:
 *
 *  - BUILD SCAN (preferred): once the game is built, read the renderer the build actually
 *    produced (apps/display/src/active-genre/*, game-art.ts) and extract the true colors,
 *    ground fraction, world scale, character colors, and asset refs. This is what makes
 *    previs match the final render — the look lives in that code, not in prose.
 *  - DESIGN GUESS (fallback, pre-build): derive a palette from the Art Direction section.
 *
 * Cached to `design/previs-skin.json`; a design-time guess is upgraded to a build scan
 * the first time a real build exists. The build agent may also write source:'build' itself.
 */
import { z } from 'zod';
import type { CrucibleClient } from '@volley/crucible-client';
import type { DesignProject } from '@volley/crucible-ipc-types';
import {
  aiChat,
  readSection,
  readPrevisSkin,
  writePrevisSkin,
  readPrevisImages,
  readBuildRender,
} from './anvil-api';
import { parseAiJson } from './schemas';

const HEX = z.string().regex(/^#[0-9a-fA-F]{3,8}$/, 'hex color like #1a2b3c');

/** How a fill is painted — reproduced on the previs canvas with the matching
 *  createLinearGradient / createRadialGradient. Mirrors the CSS a code-drawn game uses. */
const FillSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('solid'), color: HEX }),
  z.object({ type: z.literal('linear'), from: HEX, to: HEX, angle: z.number().optional() }),
  z.object({
    type: z.literal('radial'),
    inner: HEX,
    outer: HEX,
    /** Highlight centre as a 0..1 fraction of the shape box (default 0.5,0.5). */
    cx: z.number().min(0).max(1).optional(),
    cy: z.number().min(0).max(1).optional(),
  }),
]);
export type PrevisFill = z.infer<typeof FillSchema>;

/**
 * The look of ONE entity/hazard as the built game actually draws it — a "visual recipe."
 * Two sources, best-first: a real sprite PNG (`sprite`, when game-art.ts is populated), or a
 * shape + fill the previs canvas reproduces (the path for a code-drawn game like Bumble, whose
 * coins/enemies/hazards are CSS gradients, not sprites). Absent → schematic role marker.
 */
export const AppearanceSchema = z.object({
  /** Asset ref under apps/display/public — wins when present + decodable. */
  sprite: z.string().optional(),
  shape: z.enum(['disc', 'rect', 'roundRect', 'diamond', 'triangle', 'blob']).optional(),
  fill: FillSchema.optional(),
  stroke: z.object({ color: HEX, width: z.number().positive() }).optional(),
  /** Soft outer glow (e.g. a big challenge coin's warm halo). */
  glow: HEX.optional(),
  /** Display size in metres: a single value (square/diameter) or [width, height]. */
  sizeM: z
    .union([z.number().positive(), z.tuple([z.number().positive(), z.number().positive()])])
    .optional(),
  /** Grounded things (enemies, spike beds) anchor at their base; floating pickups centre. */
  anchor: z.enum(['center', 'bottom']).optional(),
});
export type Appearance = z.infer<typeof AppearanceSchema>;

/** A biome's palette override (design §20) — the environment fields a snowy/cave/etc. setting
 *  changes. All optional: an unset field falls back to the game's default palette. */
export const BiomePaletteSchema = z.object({
  sky: HEX.optional(),
  ground: HEX.optional(),
  groundEdge: HEX.optional(),
  fence: HEX.optional(),
  hazard: HEX.optional(),
  accent: HEX.optional(),
  /** Optional backdrop image ref under the built game's apps/display/public. */
  background: z.string().optional(),
});
export type BiomePalette = z.infer<typeof BiomePaletteSchema>;

export const PrevisSkinSchema = z.object({
  version: z.literal(1),
  palette: z.object({
    /** Side-scroll backdrop + ground. */
    sky: HEX,
    ground: HEX,
    groundEdge: HEX,
    fence: HEX,
    hazard: HEX,
    /** One entry per player character — drives marker count AND colors. */
    players: z.array(HEX).min(1).max(8),
    accent: HEX.optional(),
    /** Top-down plan view. */
    roomFloor: HEX,
    corridor: HEX,
    lock: HEX,
    key: HEX,
  }),
  /** Framing hints extracted from the built renderer's constants, so previs frames the
   *  scene like the TV does (design §19 Phase 3). All optional; viewport uses defaults. */
  render: z
    .object({
      /** Ground line as a fraction of viewport height (0 = top). e.g. GROUND_FRAC. */
      groundFrac: z.number().min(0).max(1).optional(),
      /** World→screen scale the game uses (e.g. PX_PER_M) — for character/scale parity. */
      pxPerM: z.number().positive().optional(),
    })
    .optional(),
  /** Phase 2 — image layers: refs into the BUILT game's apps/display/public/. */
  images: z
    .object({
      background: z.string().optional(),
      groundTile: z.string().optional(),
      playerSprite: z.string().optional(),
    })
    .optional(),
  /** Phase 3 — per-entity/per-hazard visual recipes so pickups/enemies/hazards render in the
   *  game's look, not a schematic marker. Keyed by Content-item ref FIRST (e.g. "Grumble",
   *  "Big challenge coin"), then by role ("pickup"/"enemy"/"hazard") as a fallback; the previs
   *  resolves ref → role → schematic. */
  appearance: z.record(z.string(), AppearanceSchema).optional(),
  /** Biome registry (design §20) — named environment palettes (snowy, cave, …) seeded from
   *  Art Direction or authored via "new biome". Each entry overrides the default palette's
   *  environment fields; a level references one by name and carries the resolved snapshot. */
  biomes: z.record(z.string(), BiomePaletteSchema).optional(),
  meta: z
    .object({
      style: z.string().optional(),
      /** How it was derived: art-direction guess, scan of the built renderer, or the
       *  build agent writing it directly. */
      source: z.enum(['design', 'build-scan', 'build']).optional(),
    })
    .optional(),
});
export type PrevisSkin = z.infer<typeof PrevisSkinSchema>;

/** The environment fields a biome may override on the base palette. */
const BIOME_KEYS = ['sky', 'ground', 'groundEdge', 'fence', 'hazard', 'accent'] as const;

/** Merge a level's biome overrides over the default palette (only defined biome colour fields
 *  win; the rest fall back to base). Pure — shared by the previs and any consumer. */
export function applyBiome<T extends Record<string, unknown>>(
  base: T,
  biome?: Partial<Record<(typeof BIOME_KEYS)[number], string>> | null,
): T {
  if (!biome) return base;
  const out = { ...base };
  for (const k of BIOME_KEYS) {
    const v = biome[k];
    if (v) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/** Built-in fallback palettes for common biome names — used when the AI derive is
 *  unavailable (offline, bad JSON, schema miss). Keyed by a lowercased name substring so
 *  "snowy peaks" still matches "snowy". Only the fields a biome actually changes are set;
 *  the rest fall back to the game's default palette via applyBiome. */
const BIOME_FALLBACKS: Record<string, BiomePalette> = {
  snow: {
    sky: '#cfe8f5',
    ground: '#e8eef2',
    groundEdge: '#b9cbd6',
    fence: '#9fb3c0',
    accent: '#7fbfe0',
  },
  ice: {
    sky: '#d6f0f7',
    ground: '#dbeef5',
    groundEdge: '#a9cdda',
    fence: '#8fb6c6',
    accent: '#5fc7e0',
  },
  cave: {
    sky: '#141018',
    ground: '#4a3b2e',
    groundEdge: '#2e241b',
    fence: '#5a4632',
    hazard: '#c24a2a',
    accent: '#8a6f4a',
  },
  lava: {
    sky: '#2a1410',
    ground: '#3a1c14',
    groundEdge: '#7a2a12',
    fence: '#5a2418',
    hazard: '#ff5a1a',
    accent: '#ffa62a',
  },
  forest: {
    sky: '#bfe3c0',
    ground: '#3f5a2e',
    groundEdge: '#2c451f',
    fence: '#5a4a30',
    accent: '#6fb050',
  },
  desert: {
    sky: '#f3e2b0',
    ground: '#d9b877',
    groundEdge: '#b8965a',
    fence: '#a9824c',
    accent: '#e0a850',
  },
  night: {
    sky: '#0c1030',
    ground: '#1c2038',
    groundEdge: '#12162a',
    fence: '#2a2f4a',
    accent: '#6a7bd0',
  },
};

/** Pick a built-in fallback for a biome name (substring match), else a neutral desaturated
 *  wash so the level still reads as "themed" without committing to a specific look. */
function fallbackBiome(name: string): BiomePalette {
  const n = name.toLowerCase();
  for (const [key, pal] of Object.entries(BIOME_FALLBACKS)) {
    if (n.includes(key)) return pal;
  }
  return {
    sky: '#c8ccd2',
    ground: '#8a8f97',
    groundEdge: '#6c7178',
    fence: '#7a7f86',
    accent: '#9aa0a8',
  };
}

const BIOME_JSON_SHAPE = `{"sky":"#..","ground":"#..","groundEdge":"#..","fence":"#..","hazard":"#..","accent":"#.."}`;

/** Derive a per-level BIOME palette from its NAME plus optional Art Direction prose (for tone).
 *  On ANY failure — offline, non-JSON, schema miss — falls back to a built-in default for common
 *  names (snowy/ice/cave/lava/forest/desert/night) else a neutral tint. Kept small (maxTokens 400):
 *  a biome only re-skins the environment fields, so the rest fall back to the game's palette. */
export async function deriveBiomePalette(
  client: CrucibleClient,
  gameId: string,
  name: string,
  artDirectionText?: string,
): Promise<BiomePalette> {
  try {
    const resp = await aiChat(client, {
      gameId,
      system: `You derive a per-level BIOME palette — an environment re-skin like "snowy", "cave",
or "lava" — for a 2D game's level-editor preview. Output ONLY JSON (no prose, no fences):
${BIOME_JSON_SHAPE}
Rules: every value a hex color; set ONLY the fields the biome actually changes (sky/ground/
groundEdge/fence/hazard/accent) and OMIT the rest so they fall back to the game's default
palette; make the colors evoke the biome name and stay readable on a dark editor UI.`,
      messages: [
        {
          role: 'user',
          content: `Biome: ${name}${
            artDirectionText?.trim()
              ? `\n\nArt Direction (for tone):\n${artDirectionText.slice(0, 2000)}`
              : ''
          }\n\nDerive the biome palette. Output ONLY the JSON.`,
        },
      ],
      maxTokens: 400,
    });
    return parseAiJson(resp.text, BiomePaletteSchema);
  } catch {
    return fallbackBiome(name);
  }
}

/** Decoded image layers for the viewports (loaded from data URLs over IPC). */
export interface PrevisImages {
  background?: HTMLImageElement;
  groundTile?: HTMLImageElement;
  playerSprite?: HTMLImageElement;
  /** Decoded entity sprites, keyed by their asset ref (as appearance.sprite names them). */
  sprites?: Record<string, HTMLImageElement>;
}

/** Every distinct image ref the skin points at (layers + any entity-appearance sprites). */
function skinImageRefs(skin: PrevisSkin): string[] {
  const refs = Object.values(skin.images ?? {}).filter((r): r is string => Boolean(r));
  for (const app of Object.values(skin.appearance ?? {})) {
    if (app.sprite) refs.push(app.sprite);
  }
  return Array.from(new Set(refs));
}

/** Fetch + decode the skin's image refs (built game's public dir → data URLs).
 *  Missing/oversized/unreadable refs resolve to undefined — palette/recipe is the fallback. */
export async function loadPrevisImages(
  client: CrucibleClient,
  gameId: string,
  skin: PrevisSkin,
): Promise<PrevisImages | null> {
  const refs = skinImageRefs(skin);
  if (refs.length === 0) return null;
  const data = await readPrevisImages(client, gameId, refs).catch(
    () => ({}) as Record<string, string>,
  );
  const decode = (ref?: string): Promise<HTMLImageElement | undefined> =>
    new Promise((resolve) => {
      const url = ref ? data[ref] : undefined;
      if (!url) return resolve(undefined);
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(undefined);
      img.src = url;
    });
  const [background, groundTile, playerSprite] = await Promise.all([
    decode(skin.images?.background),
    decode(skin.images?.groundTile),
    decode(skin.images?.playerSprite),
  ]);
  // Entity sprites (keyed by ref) — only the ones that actually decoded.
  const sprites: Record<string, HTMLImageElement> = {};
  const spriteRefs = Array.from(
    new Set(
      Object.values(skin.appearance ?? {})
        .map((a) => a.sprite)
        .filter((r): r is string => Boolean(r)),
    ),
  );
  await Promise.all(
    spriteRefs.map(async (ref) => {
      const img = await decode(ref);
      if (img) sprites[ref] = img;
    }),
  );
  const hasSprites = Object.keys(sprites).length > 0;
  if (!background && !groundTile && !playerSprite && !hasSprites) return null;
  return { background, groundTile, playerSprite, sprites: hasSprites ? sprites : undefined };
}

const SKIN_JSON_SHAPE = `{"version":1,"palette":{"sky":"#..","ground":"#..","groundEdge":"#..",
"fence":"#..","hazard":"#..","players":["#..",...],"accent":"#..","roomFloor":"#..",
"corridor":"#..","lock":"#..","key":"#.."},"render":{"groundFrac":<0..1>,"pxPerM":<number>},
"images":{"background":"<public ref>","groundTile":"<ref>","playerSprite":"<ref>"},
"appearance":{"<Content-item ref or role>":{"sprite":"<public ref, only if game ships PNGs>",
"shape":"disc|rect|roundRect|diamond|triangle|blob","fill":{"type":"solid|linear|radial",
"color":"#..","from":"#..","to":"#..","angle":<deg>,"inner":"#..","outer":"#..","cx":<0..1>,"cy":<0..1>},
"stroke":{"color":"#..","width":<n>},"glow":"#..","sizeM":<m or [w,h]>,"anchor":"center|bottom"}},
"meta":{"style":"<one line>","source":"<see below>"}}`;

/** Derive the skin by reading the renderer the build ACTUALLY produced — the intelligent
 *  path: colors/scale/ground come from the real code, not a prose guess. */
async function deriveFromBuild(
  client: CrucibleClient,
  gameId: string,
  project: DesignProject,
  source: string,
): Promise<PrevisSkin | null> {
  try {
    const resp = await aiChat(client, {
      gameId,
      system: `You extract a PREVIS SKIN from a built game's ACTUAL renderer source so a level
editor preview matches the final render. You are given the game's display renderer
(active-genre/*) and its generated art manifest (game-art.ts). Output ONLY JSON, no fences:
${SKIN_JSON_SHAPE}
Rules:
- Read the REAL values from the code. palette colors = the actual color constants / fill
  styles the renderer uses (e.g. a RUNNER_COLOR constant, sky/ground fills or gradients).
- "players" = the actual per-character colors the renderer draws (one entry per character;
  a single color for a solo game). Never invent colors the code doesn't use.
- render.groundFrac = the ground-line fraction constant if present (e.g. GROUND_FRAC);
  render.pxPerM = the world→pixel scale constant if present (e.g. PX_PER_M). Omit if absent.
- images: ONLY if game-art.ts's gameArtByRole lists assets — map role "background" →
  images.background (its assetUrl path under apps/display/public), a hero/player sprite →
  images.playerSprite. Omit images entirely when the manifest is an empty stub.
- appearance (KEY for fidelity): a map from each drawable entity/hazard to how the renderer
  ACTUALLY draws it, so the preview reproduces the real look. Read the draw code:
  * For each thing the game places on the course — pickups (coins), enemies, and each hazard
    KIND (spike bed, geyser, moving block, swinging platform, etc.) — emit one entry.
  * KEY it by the Content-item name it corresponds to when you can infer it (e.g. "Grumble",
    "Coin", "Big challenge coin", "Spike bed"); otherwise key by role ("pickup","enemy","hazard").
  * If the renderer draws it with a SPRITE (game-art.ts populated) → set "sprite" to that ref.
  * If it is CODE-DRAWN (CSS/canvas gradients + shapes, as most current games are) → translate
    the actual gradient into "fill": a radial-gradient → {"type":"radial","inner":<centre color>,
    "outer":<edge color>,"cx":<highlight x 0..1>,"cy":<y 0..1>}; a linear-gradient →
    {"type":"linear","from":..,"to":..,"angle":<deg>}; a flat fill → {"type":"solid","color":..}.
    Pick "shape" (disc for round coins/orbs, rect/roundRect for beds/blocks/platforms, triangle
    for spikes, blob/diamond for creatures). Use the REAL colors from PALETTE/constants, not
    invented ones. Add "glow" for a visible halo, "stroke" for a border, "sizeM" from the
    rendered size vs pxPerM (a coin ~0.5, an enemy ~1, a platform its width), "anchor":"bottom"
    for grounded things and "center" for floating pickups.
  * Omit an entry rather than guess. Omit "appearance" entirely only if nothing is drawable.
- Every palette value must be a hex color; fill missing roles with the closest on-theme
  color from the code. Set meta.source to "build-scan".`,
      messages: [
        {
          role: 'user',
          content: `Game: ${project.name}\n\nBuilt renderer source:\n${source}\n\nExtract the previs skin. Output ONLY the JSON.`,
        },
      ],
      // Headroom for the appearance map (a recipe per entity/hazard kind) on top of the
      // palette/render/images — too low truncates the JSON, fails parseAiJson, and disables
      // the whole skin (returns null → schematic markers).
      maxTokens: 3000,
    });
    const skin = parseAiJson(resp.text, PrevisSkinSchema);
    const withSource: PrevisSkin = { ...skin, meta: { ...skin.meta, source: 'build-scan' } };
    await writePrevisSkin(client, gameId, withSource).catch(() => undefined);
    return withSource;
  } catch {
    return null;
  }
}

/** Derive a palette from the Art Direction prose — the pre-build fallback. */
async function deriveFromArtDirection(
  client: CrucibleClient,
  gameId: string,
  project: DesignProject,
): Promise<PrevisSkin | null> {
  const artSection = project.sections.find((s) => s.id.toLowerCase().includes('art'));
  if (!artSection) return null;
  let art = '';
  try {
    art = await readSection(client, gameId, artSection.filename);
  } catch {
    return null;
  }
  if (!art.trim()) return null;

  try {
    const resp = await aiChat(client, {
      gameId,
      system: `You derive a structured PREVIS PALETTE from a game's Art Direction so the level
editor's preview matches the game's look. Output ONLY JSON (no prose, no fences):
${SKIN_JSON_SHAPE}
Rules: every palette value a hex color; use the art direction's OWN named hexes/colors where
given (which color plays which ROLE is your judgment call); "players" gets one entry per
player character the design describes (1 for single-player); readable on a dark editor UI.
Omit render/images/appearance (no build to read them from). Set meta.source to "design".`,
      messages: [
        {
          role: 'user',
          content: `Game: ${project.name}\n\nArt Direction:\n${art.slice(0, 6000)}\n\nDerive the previs palette. Output ONLY the JSON.`,
        },
      ],
      maxTokens: 512,
    });
    const skin = parseAiJson(resp.text, PrevisSkinSchema);
    const withSource: PrevisSkin = { ...skin, meta: { ...skin.meta, source: 'design' } };
    await writePrevisSkin(client, gameId, withSource).catch(() => undefined);
    return withSource;
  } catch {
    return null;
  }
}

/**
 * Load the cached skin, or derive one. Preference order: a build scan of the real renderer
 * (when the game is built) beats an Art-Direction guess. A cached design-time guess is
 * UPGRADED to a build scan the first time a build exists. Returns null when there's
 * nothing to derive from (previs falls back to the schematic defaults).
 */
export async function loadOrDerivePrevisSkin(args: {
  client: CrucibleClient;
  gameId: string;
  project: DesignProject;
}): Promise<PrevisSkin | null> {
  const { client, gameId, project } = args;
  const cached = PrevisSkinSchema.safeParse(await readPrevisSkin(client, gameId).catch(() => null));
  const buildSource = await readBuildRender(client, gameId).catch(() => '');
  const hasBuild = buildSource.trim().length > 0;

  if (cached.success) {
    const src = cached.data.meta?.source;
    // A pre-build design guess is stale once a real build exists — re-derive from it.
    // Build-sourced skins (scan or agent-written) and pre-build guesses stand as cached.
    if (!(hasBuild && src === 'design')) return cached.data;
  }

  if (hasBuild) {
    const scanned = await deriveFromBuild(client, gameId, project, buildSource);
    if (scanned) return scanned;
  }
  // No build (or the scan failed): fall back to the Art-Direction palette, unless we
  // already have a usable cached guess to keep.
  if (cached.success) return cached.data;
  return deriveFromArtDirection(client, gameId, project);
}
